/**
 * Tracks in-flight client navigations to `/p/[projectId]`.
 *
 * Set synchronously on project click so AppShell can show the boot loader
 * immediately — before Next.js finishes downloading the editor chunk.
 */

import { create } from 'zustand'

interface ProjectNavigationState {
  isNavigating: boolean
  /** Pathname when the navigation started (e.g. /projects), for abandonment detection. */
  originPathname: string | null
  /**
   * `performance.now()` at the click that started the navigation. An
   * `arrived` end keeps it so `canvas_ready` can measure an in-document
   * project switch from the moment the user asked for it (consumed by
   * `measureProjectOpen`); an `abandoned` end — Back, a redirect, the stuck
   * timeout — clears it, so a later arrival by history traversal is not
   * measured against a click that never led anywhere.
   */
  startedAt: number | null
  startNavigation: () => void
  endNavigation: (outcome?: 'arrived' | 'abandoned') => void
  consumeStartedAt: () => number | null
}

export const useProjectNavigationStore = create<ProjectNavigationState>((set, get) => ({
  isNavigating: false,
  originPathname: null,
  startedAt: null,
  startNavigation: () =>
    set({
      isNavigating: true,
      originPathname: typeof window !== 'undefined' ? window.location.pathname : null,
      startedAt: typeof performance !== 'undefined' ? performance.now() : null,
    }),
  endNavigation: (outcome = 'abandoned') =>
    set(state => ({
      isNavigating: false,
      originPathname: null,
      startedAt: outcome === 'arrived' ? state.startedAt : null,
    })),
  consumeStartedAt: () => {
    const { startedAt } = get()
    if (startedAt !== null) set({ startedAt: null })
    return startedAt
  },
}))

export function startProjectNavigation(): void {
  useProjectNavigationStore.getState().startNavigation()
}

/** The editor page mounted for the navigation: the overlay ends, the click mark stays for `canvas_ready`. */
export function endProjectNavigation(): void {
  useProjectNavigationStore.getState().endNavigation('arrived')
}
